import { RestApiMethodName } from 'lib/enum/rest-api-method';
import { HttpResponseError } from 'lib/error/http-response-error';
import pRetry, { AbortError } from 'p-retry';
import { mergeDeepLeft } from 'ramda';
import { ABORT_CONTROLLER_SIGNAL_ABORTED_MESSAGE } from '../config';
import { parseJSON, parseSettings, checkStatus, createHttpResponseError } from './utils';
import queryString from 'query-string';
import { config } from 'config';

export class PublicApi {
    protected settings: Record<string, any>;

    constructor() {
        this.settings = {};
    }

    protected parseEndpoint(endpoint: string, params?: any): string {
        const url = endpoint.indexOf('http') === 0 ? endpoint : config.apiUrl + endpoint;
        const querystring = params ? `?${queryString.stringify(params, { arrayFormat: 'bracket' })}` : '';
        return `${url}${querystring}`;
    }

    protected async apiRequest<T>(
        endpoint: string,
        { params, ...settings }: any = {},
        expectNoResponse?: boolean,
    ): Promise<T> {
        try {
            const response = await fetch(this.parseEndpoint(endpoint, params), parseSettings(settings));

            const checkedResponse = await checkStatus(response);
            if (settings.returnRawResponse !== 'undefined' && settings.returnRawResponse === true) {
                return checkedResponse as any;
            }

            if (expectNoResponse) return checkedResponse as T;

            return await parseJSON(checkedResponse);
        } catch (err: any) {
            if (settings?.signal?.aborted) {
                throw new Error(ABORT_CONTROLLER_SIGNAL_ABORTED_MESSAGE);
            }

            if (err instanceof HttpResponseError || err instanceof AbortError) {
                return Promise.reject(err);
            }

            const errorMessage = 'Unknown error occurred';
            return Promise.reject(
                createHttpResponseError(errorMessage, { status: null } as any, { message: errorMessage }),
            );
        }
    }

    private request<T>(endpoint: string, settings: any, expectNoResponse?: boolean): Promise<T> {
        return settings.method === RestApiMethodName.GET
            ? pRetry(
                  () =>
                      this.apiRequest<T>(
                          endpoint,
                          mergeDeepLeft(this.settings, { method: RestApiMethodName.GET, ...settings }),
                          expectNoResponse,
                      ),
                  {
                      retries: 2,
                  },
              )
            : this.apiRequest<T>(endpoint, mergeDeepLeft(this.settings, settings), expectNoResponse);
    }

    public post<T>(endpoint: string, data: any = {}, settings?: any, expectNoResponse?: boolean): Promise<T> {
        return this.request<T>(endpoint, { method: RestApiMethodName.POST, data, ...settings }, expectNoResponse);
    }

    public get<T>(endpoint: string, settings?: any): Promise<T> {
        return this.request<T>(endpoint, { method: RestApiMethodName.GET, ...settings });
    }

    public put<T>(endpoint: string, data: any, settings?: any, expectNoResponse?: boolean): Promise<T> {
        return this.request<T>(endpoint, { method: RestApiMethodName.PUT, data, ...settings }, expectNoResponse);
    }

    public patch<T>(endpoint: string, data: any, settings?: any): Promise<T> {
        return this.request<T>(endpoint, { method: RestApiMethodName.PATCH, data, ...settings });
    }

    public delete<T>(endpoint: string, settings?: any): Promise<T> {
        return this.request<T>(endpoint, { method: RestApiMethodName.DELETE, ...settings });
    }
}

export const publicApi = new PublicApi();
